import { useEffect, useState } from 'react';

/**
 * Subscribes to a CSS media query from JS.
 *
 * Some layout decisions can't be made in CSS alone — an SVG chart has to
 * know whether it is drawing its outside labels before it computes their
 * positions, and a media query in a stylesheet can only hide them after the
 * fact, leaving the geometry sized for labels that aren't there.
 *
 * @param {string} query e.g. '(max-width: 720px)'
 * @returns {boolean} whether the query currently matches
 */
export const useMediaQuery = (query) => {
  // Read synchronously on first render so the chart never paints its wide
  // layout and then snap to the narrow one. Guarded for environments with no
  // matchMedia (SSR, older test runners).
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};

export default useMediaQuery;
